/**
 * useWakeWord
 *
 * Détecte le mot de réveil « saisi » via WebSocket + Vosk local (RGPD-compliant).
 * Remplace l'ancienne implémentation Web Speech API (envoyait audio à Google).
 *
 * Protocole :
 *   - Ouvre ws://localhost:8100/ws/wakeword?token=...
 *   - Stream audio PCM 16kHz mono en continu
 *   - Le backend accumule 1s d'audio → Vosk → détecte « saisi »
 *   - Reçoit {"step": "wakeword_detected"} → appelle onWakeWord()
 *   - Reconnecte automatiquement si la connexion se coupe
 *
 * pause() / resume() : suspend l'envoi audio pendant la dictée pour éviter
 * le double flux micro (wakeword + voice session simultanés).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { authService } from '@/features/auth/services/authService';
import { API_ENDPOINTS } from '@/config/api';

interface UseWakeWordOptions {
  enabled: boolean;
  onWakeWord: () => void;
  /** Barge-in (V2) : "stop" détecté sur le flux wake (pendant le TTS). Optionnel. */
  onStopWord?: () => void;
  /**
   * Mode commande (Q-1 audit §7.4) : transcript renvoyé par la route wake pendant
   * les états de contrôle (armé via `armCommand`). Le front parse + dédup. Optionnel.
   */
  onCommand?: (text: string) => void;
}

export function useWakeWord({ enabled, onWakeWord, onStopWord, onCommand }: UseWakeWordOptions) {
  const [isListening, setIsListening] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const activeRef = useRef(false);
  /** true = envoi audio actif, false = suspendu pendant dictée */
  const sendingRef = useRef(true);
  /** Mode commande désiré (Q-1) : (re)émis à l'ouverture du WS → robuste à la race
   *  « arm avant WS prêt » (post-capture) et aux reconnexions automatiques. */
  const commandModeRef = useRef(false);
  const onWakeWordRef = useRef(onWakeWord);
  useEffect(() => { onWakeWordRef.current = onWakeWord; }, [onWakeWord]);
  const onStopWordRef = useRef(onStopWord);
  useEffect(() => { onStopWordRef.current = onStopWord; }, [onStopWord]);
  const onCommandRef = useRef(onCommand);
  useEffect(() => { onCommandRef.current = onCommand; }, [onCommand]);

  const cleanupAudio = useCallback(() => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    activeRef.current = false;
    setIsListening(false);
    cleanupAudio();
    wsRef.current?.close(1000);
    wsRef.current = null;
  }, [cleanupAudio]);

  const start = useCallback(async () => {
    const token = authService.getToken();
    if (!token || !activeRef.current) return;

    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });

      if (!activeRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        return;
      }

      const ws = new WebSocket(API_ENDPOINTS.wakewordWs(token));
      wsRef.current = ws;

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve();
        ws.onerror = () => reject(new Error('WebSocket wakeword failed'));
      });

      // (Q-1) Ré-émet le mode commande désiré si armé pendant la (re)connexion —
      // sinon un arm survenu avant l'ouverture du WS serait perdu (hang silencieux).
      if (commandModeRef.current) {
        ws.send(JSON.stringify({ action: 'command', on: true }));
      }

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data as string);
          if (msg.step === 'wakeword_detected') {
            onWakeWordRef.current();
          } else if (msg.step === 'stop_detected') {
            onStopWordRef.current?.();
          } else if (msg.step === 'command') {
            onCommandRef.current?.((msg.text as string) ?? '');
          }
        } catch { /* ignore */ }
      };

      ws.onclose = () => {
        // Si toujours actif (fermeture inattendue), nettoyer l'audio et reconnecter
        if (activeRef.current) {
          cleanupAudio();
          wsRef.current = null;
          setIsListening(false);
          setTimeout(() => {
            if (activeRef.current) start();
          }, 1000);
        }
      };

      const ctx = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(streamRef.current);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (ws.readyState !== WebSocket.OPEN || !sendingRef.current) return;
        const float32 = e.inputBuffer.getChannelData(0);
        const pcm = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          const s = Math.max(-1, Math.min(1, float32[i]));
          pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        ws.send(pcm.buffer);
      };

      source.connect(processor);
      processor.connect(ctx.destination);
      setIsListening(true);

    } catch {
      cleanupAudio();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setIsListening(false);
    }
  }, [cleanupAudio]);

  const pause = useCallback(() => { sendingRef.current = false; }, []);
  const resume = useCallback(() => { sendingRef.current = true; }, []);

  /** Bascule le backend wake en mode commande (renvoi du transcript) / hors mode.
   *  No-op si le WS n'est pas ouvert (re-arm naturel à la prochaine entrée d'état). */
  const setCommand = useCallback((on: boolean) => {
    commandModeRef.current = on;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'command', on }));
    }
  }, []);
  const armCommand = useCallback(() => setCommand(true), [setCommand]);
  const disarmCommand = useCallback(() => setCommand(false), [setCommand]);

  useEffect(() => {
    if (enabled) {
      activeRef.current = true;
      sendingRef.current = true;
      start();
    } else {
      stop();
    }
    return () => stop();
  }, [enabled, start, stop]);

  return { isListening, pause, resume, armCommand, disarmCommand };
}
